import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../hooks/useStore";
import {
  Building,
  Shield,
  Lock,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { toast } from "react-toastify";
import { AuthHeroSection } from "../../components/auth/AuthHeroSection";
import { PasswordForm } from "../../components/auth/PasswordForm";
import type { PasswordFormData, ResetPasswordRequest } from "../../types/index";
import { resetPassword, clearError } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }

    setTokenValid(true);
  }, [token]);

  const onSubmit = async (formData: PasswordFormData) => {
    if (!token) return;

    setLoading(true);
    try {
      const payload: ResetPasswordRequest = {
        token,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };

      await dispatch(resetPassword(payload)).unwrap();
      setIsSuccess(true);
      toast.success(t("resetPassword.successToast"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("resetPassword.failedToast"),
      );
    } finally {
      setLoading(false);
      dispatch(clearError());
    }
  };

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex">
        <AuthHeroSection
          brandIcon={AlertTriangle}
          brandTitle={t("resetPassword.invalidLink")}
          mainTitle={t("resetPassword.linkExpired")}
          highlightedTitle=""
          description={t("resetPassword.linkExpiredDescription")}
          features={[
            {
              icon: Shield,
              title: t("resetPassword.invalidHero.feature1Title"),
              description: t("resetPassword.invalidHero.feature1Desc"),
            },
            {
              icon: Lock,
              title: t("resetPassword.invalidHero.feature2Title"),
              description: t("resetPassword.invalidHero.feature2Desc"),
            },
          ]}
          colorScheme="primary"
        />

        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md text-center">
            <div className="bg-white rounded-2xl border border-[rgba(11,15,14,0.08)] p-8 shadow-sm">
              <div className="mx-auto h-16 w-16 bg-[#fdf5ec] rounded-xl flex items-center justify-center mb-6">
                <AlertTriangle className="h-8 w-8 text-[#C97A3C]" />
              </div>
              <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-4">
                {t("resetPassword.invalidLink")}
              </h2>
              <p className="text-[#8A9590] mb-8">
                {t("resetPassword.linkExpiredMessage")}
              </p>
              <Link
                to="/forgot-password"
                className="inline-block w-full bg-[#C97A3C] hover:bg-[#B86B30] text-white py-3 rounded-lg transition-all duration-200 font-medium text-center"
              >
                {t("resetPassword.requestNewLink")}
              </Link>
              <Link
                to="/login"
                className="inline-block w-full mt-4 text-[#C97A3C] hover:text-[#B86B30] transition-colors font-medium text-center"
              >
                {t("resetPassword.backToSignIn")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex">
        <AuthHeroSection
          brandIcon={CheckCircle}
          brandTitle={t("resetPassword.successHero.brandTitle")}
          mainTitle={t("resetPassword.successHero.mainTitle")}
          highlightedTitle={t("resetPassword.successHero.highlightedTitle")}
          description={t("resetPassword.successHero.description")}
          features={[
            {
              icon: Shield,
              title: t("resetPassword.successHero.feature1Title"),
              description: t("resetPassword.successHero.feature1Desc"),
            },
            {
              icon: Lock,
              title: t("resetPassword.successHero.feature2Title"),
              description: t("resetPassword.successHero.feature2Desc"),
            },
          ]}
          colorScheme="success"
        />

        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md text-center">
            <div className="bg-white rounded-2xl border border-[rgba(11,15,14,0.08)] p-8 shadow-sm">
              <div className="mx-auto h-16 w-16 bg-[#e6f7ef] rounded-xl flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-[#00A862]" />
              </div>
              <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-4">
                {t("resetPassword.successTitle")}
              </h2>
              <p className="text-[#8A9590] mb-8">
                {t("resetPassword.successMessage")}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-[#C97A3C] hover:bg-[#B86B30] text-white py-3 rounded-lg transition-all duration-200 font-medium shadow-sm"
              >
                {t("resetPassword.continueToSignIn")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex">
      <AuthHeroSection
        brandIcon={Lock}
        brandTitle={t("resetPassword.hero.brandTitle")}
        mainTitle={t("resetPassword.hero.mainTitle")}
        highlightedTitle={t("resetPassword.hero.highlightedTitle")}
        description={t("resetPassword.hero.description")}
        features={[
          {
            icon: Shield,
            title: t("resetPassword.hero.feature1Title"),
            description: t("resetPassword.hero.feature1Desc"),
          },
          {
            icon: Lock,
            title: t("resetPassword.hero.feature2Title"),
            description: t("resetPassword.hero.feature2Desc"),
          },
          {
            icon: CheckCircle,
            title: t("resetPassword.hero.feature3Title"),
            description: t("resetPassword.hero.feature3Desc"),
          },
        ]}
      />

      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="mx-auto flex items-center justify-center gap-3 mb-4">
              <svg width="36" height="36" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="22" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
                <rect x="27" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="32" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="38" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="44" y="18" width="4" height="64" fill="#E89D5C"/>
                <rect x="51" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="57" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="63" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="68" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
              </svg>
              <h1 className="text-2xl font-medium tracking-tight text-[#0B0F0E]">
                LiveCargo<span className="text-[#C97A3C]">.</span>
              </h1>
            </div>
          </div>

          <PasswordForm
            onSubmit={onSubmit}
            loading={loading}
            title={t("resetPassword.title")}
            description={t("resetPassword.description")}
            submitButtonText={t("resetPassword.submitButton")}
          />

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-[#C97A3C] hover:text-[#B86B30] transition-colors"
            >
              {t("resetPassword.backToSignIn")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
